import {
  closeAccountDatabase,
  openAccountDatabase,
  requireAccountDatabase,
  type DBMessage,
  type DBSession,
  type DBUser,
  type DBGroup,
} from './database';
import type { ChatMessage, ChatSession } from '@domain/chat/types';
import { buildMessageIdentity } from '@domain/chat/outgoingMessageState.js';
import { parseFileMessagePayload } from '@domain/file/messagePayload';
import type { FileTransferLifecycleSnapshot } from '@domain/file/api';
import type { FileTransferSyncChange } from '@domain/file/api';

export class ChatRepository {
  async openForOwner(ownerUserId: number): Promise<void> {
    await openAccountDatabase(ownerUserId);
  }

  close(): void {
    closeAccountDatabase();
  }

  private static dedupeMessages(messages: ChatMessage[]): ChatMessage[] {
    const seen = new Set<string>();
    const deduped: ChatMessage[] = [];

    for (const message of messages) {
      const identity = buildMessageIdentity(message);
      if (seen.has(identity)) {
        continue;
      }
      seen.add(identity);
      deduped.push(message);
    }

    return deduped;
  }

  private static dbMessageToChatMessage(dbMsg: DBMessage): ChatMessage {
    return {
      localId: dbMsg.localId,
      msgId: dbMsg.msgId,
      sessionKey: dbMsg.sessionKey,
      sessionId: dbMsg.sessionId,
      sessionType: dbMsg.sessionType,
      from: dbMsg.from,
      text: dbMsg.text,
      msgType: dbMsg.msgType,
      msgData: dbMsg.msgData,
      fileMeta: parseFileMessagePayload(dbMsg.msgData, dbMsg.msgType),
      audioMimeType: dbMsg.audioMimeType,
      ts: dbMsg.ts,
      dir: dbMsg.dir,
      status: dbMsg.status,
    };
  }

  private static chatMessageToDBMessage(msg: ChatMessage): DBMessage {
    return {
      localId: msg.localId,
      msgId: msg.msgId,
      sessionKey: msg.sessionKey,
      sessionId: msg.sessionId,
      sessionType: msg.sessionType,
      from: msg.from,
      text: msg.text,
      msgType: msg.msgType,
      msgData: msg.msgData,
      audioMimeType: msg.audioMimeType,
      ts: msg.ts,
      dir: msg.dir,
      status: msg.status,
      displayType: 1,
      loadStatus: 1,
    };
  }

  async saveMessage(message: ChatMessage): Promise<boolean> {
    const dbMessage = ChatRepository.chatMessageToDBMessage(message);
    const db = requireAccountDatabase();
    const result = await db.insertOrIgnoreMessage(dbMessage);
    return result.inserted;
  }

  async saveMessages(messages: ChatMessage[]): Promise<number> {
    const dbMessages = messages.map(ChatRepository.chatMessageToDBMessage);
    return requireAccountDatabase().bulkInsertMessages(dbMessages);
  }

  async getFileTransferLifecycles(
    transferIds: string[],
  ): Promise<Record<string, FileTransferLifecycleSnapshot>> {
    if (transferIds.length === 0) return {};
    const rows = await requireAccountDatabase().fileTransferLifecycles
      .where('transferId')
      .anyOf(transferIds)
      .toArray();
    return Object.fromEntries(rows.map(({ updatedAtMs: _, ...snapshot }) => [
      snapshot.transferId,
      snapshot,
    ]));
  }

  async saveFileTransferLifecycle(
    snapshot: FileTransferLifecycleSnapshot,
  ): Promise<boolean> {
    const table = requireAccountDatabase().fileTransferLifecycles;
    return requireAccountDatabase().transaction('rw', table, async () => {
      const current = await table.get(snapshot.transferId);
      if (current && current.version >= snapshot.version) return false;
      await table.put({
        ...snapshot,
        updatedAtMs: Date.now(),
      });
      return true;
    });
  }

  async getFileTransferSyncCursor(ownerUserId: number): Promise<number> {
    return (await requireAccountDatabase().fileTransferSyncState.get(ownerUserId))?.cursor ?? 0;
  }

  async applyFileTransferSync(
    ownerUserId: number,
    changes: FileTransferSyncChange[],
    nextCursor: number,
  ): Promise<void> {
    const db = requireAccountDatabase();
    await db.transaction('rw', db.fileTransferLifecycles, db.fileTransferSyncState, async () => {
      for (const change of changes) {
        const current = await db.fileTransferLifecycles.get(change.projection.transferId);
        if (!current || current.version < change.projection.version) {
          await db.fileTransferLifecycles.put({ ...change.projection, updatedAtMs: Date.now() });
        }
      }
      const currentCursor = (await db.fileTransferSyncState.get(ownerUserId))?.cursor ?? 0;
      if (nextCursor >= currentCursor) {
        await db.fileTransferSyncState.put({ ownerUserId, cursor: nextCursor, updatedAtMs: Date.now() });
      }
    });
  }

  async getMessagesBySession(sessionKey: string, limit: number = 50, beforeTime?: number): Promise<ChatMessage[]> {
    const dbMessages = await requireAccountDatabase().getMessagesBySession(sessionKey, limit, beforeTime);
    return ChatRepository.dedupeMessages(dbMessages.map(ChatRepository.dbMessageToChatMessage));
  }

  async updateMessageWithServerId(localId: string, serverMsgId: number): Promise<boolean> {
    const db = requireAccountDatabase();
    const message = await db.findMessageByLocalId(localId);
    if (message && message.msgId === 0) {
      await db.updateMessageMsgId(localId, serverMsgId);
      return true;
    }
    return false;
  }

  async updateMessageStatus(localId: string, status: ChatMessage['status'], msgId?: number): Promise<boolean> {
    const db = requireAccountDatabase();
    const message = await db.findMessageByLocalId(localId);
    if (message) {
      await db.updateMessageStatus(localId, status, msgId);
      return true;
    }
    return false;
  }

  async messageExists(sessionKey: string, msgId: number): Promise<boolean> {
    if (msgId === 0) return false;
    const existing = await requireAccountDatabase().findMessageByMsgId(sessionKey, msgId);
    return !!existing;
  }

  async saveSession(session: ChatSession): Promise<void> {
    const dbSession: DBSession = {
      sessionKey: session.sessionKey,
      sessionId: session.sessionId,
      sessionType: session.sessionType,
      lastMsg: session.lastMsgPreview,
      updatedTime: session.updatedAt,
      unreadCnt: session.unreadCount,
      latestMsgId: session.lastMsgId,
    };
    await requireAccountDatabase().upsertSession(dbSession);
  }

  async getAllSessions(): Promise<ChatSession[]> {
    const dbSessions = await requireAccountDatabase().getAllSessions();
    return dbSessions.map(dbSession => ({
      sessionKey: dbSession.sessionKey,
      sessionId: dbSession.sessionId,
      sessionType: dbSession.sessionType,
      title: '',
      subtitle: '',
      avatarText: '',
      updatedAt: dbSession.updatedTime,
      lastMsgId: dbSession.latestMsgId,
      lastMsgPreview: dbSession.lastMsg || '',
      lastMsgFromUserId: 0,
      unreadCount: dbSession.unreadCnt,
      messages: [],
      historyState: 'idle',
      historyError: null,
      hasMoreHistory: true,
    }));
  }

  async getSession(sessionKey: string): Promise<ChatSession | null> {
    const dbSession = await requireAccountDatabase().getSession(sessionKey);
    if (!dbSession) return null;

    const messages = await this.getMessagesBySession(sessionKey, 20);
    return {
      sessionKey: dbSession.sessionKey,
      sessionId: dbSession.sessionId,
      sessionType: dbSession.sessionType,
      title: '',
      subtitle: '',
      avatarText: '',
      updatedAt: dbSession.updatedTime,
      lastMsgId: dbSession.latestMsgId,
      lastMsgPreview: dbSession.lastMsg || '',
      lastMsgFromUserId: messages.length > 0 ? messages[0].from : 0,
      unreadCount: dbSession.unreadCnt,
      messages: messages,
      historyState: 'ready',
      historyError: null,
      hasMoreHistory: messages.length >= 20,
    };
  }

  async clearSessionMessages(sessionKey: string): Promise<void> {
    await requireAccountDatabase().clearSessionMessages(sessionKey);
  }

  async deleteSession(sessionKey: string): Promise<void> {
    const db = requireAccountDatabase();
    await db.transaction('rw', db.sessions, db.messages, async () => {
      await db.clearSessionMessages(sessionKey);
      await db.deleteSession(sessionKey);
    });
  }

  async getSessionLatestMsgId(sessionKey: string): Promise<number> {
    return await requireAccountDatabase().getSessionLatestMsgId(sessionKey);
  }

  async getMsgIdsInRange(sessionKey: string, msgIdBeg: number, msgIdEnd: number): Promise<number[]> {
    return await requireAccountDatabase().getMsgIdsInRange(sessionKey, msgIdBeg, msgIdEnd);
  }

  async findOptimisticMessageForEcho(
    sessionKey: string,
    fromUserId: number,
    text: string,
    timestamp: number,
    timeWindow: number = 30000
  ): Promise<ChatMessage | null> {
    const messages = await requireAccountDatabase().getMessagesBySession(sessionKey, 50);
    for (const msg of messages) {
      if (
        msg.msgId === 0 &&
        msg.sessionKey === sessionKey &&
        msg.from === fromUserId &&
        msg.text === text &&
        msg.dir === 'out' &&
        Math.abs(msg.ts - timestamp) < timeWindow
      ) {
        return ChatRepository.dbMessageToChatMessage(msg);
      }
    }
    return null;
  }

  async saveUsers(users: DBUser[]): Promise<void> {
    for (const user of users) {
      await requireAccountDatabase().upsertUser(user);
    }
  }

  async getAllUsers(): Promise<DBUser[]> {
    return await requireAccountDatabase().getAllUsers();
  }

  async saveGroups(groups: DBGroup[]): Promise<void> {
    for (const group of groups) {
      await requireAccountDatabase().upsertGroup(group);
    }
  }

  async getAllGroups(): Promise<DBGroup[]> {
    return await requireAccountDatabase().getAllGroups();
  }
}

export const chatRepository = new ChatRepository();
